import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { Box, Typography, Chip, useTheme } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface HorizontalBarChartProps {
  data: {
    auditType: string;
    overallScore: number;
    sessionId?: number;
  }[];
  title?: string;
  selectedAudit?: string | null;
  onBarClick?: (auditType: string, sessionId?: number) => void;
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({ 
  data, 
  title = "Review Performance Overview",
  selectedAudit,
  onBarClick 
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Color based on score
  const getBarColor = (score: number, isSelected: boolean) => {
    if (isSelected) {
      // Brighter/highlighted version when selected
      if (score >= 75) return 'rgba(34, 197, 94, 1)';
      if (score >= 50) return 'rgba(245, 158, 11, 1)';
      return 'rgba(239, 68, 68, 1)';
    }
    // Normal colors
    if (score >= 75) return 'rgba(34, 197, 94, 0.8)';
    if (score >= 50) return 'rgba(245, 158, 11, 0.8)';
    return 'rgba(239, 68, 68, 0.8)';
  };

  const getBorderColor = (score: number, isSelected: boolean) => {
    if (isSelected) {
      return '#1e40af'; // Blue border for selected
    }
    if (score >= 75) return 'rgba(22, 163, 74, 1)';
    if (score >= 50) return 'rgba(217, 119, 6, 1)';
    return 'rgba(220, 38, 38, 1)';
  };

  // Sort by score descending for better visualization
  const sortedData = [...data].sort((a, b) => b.overallScore - a.overallScore);

  const chartData = {
    labels: sortedData.map(d => {
      // Truncate long names but keep enough to identify
      const name = d.auditType;
      return name.length > 35 ? name.substring(0, 35) + '...' : name;
    }),
    datasets: [{
      label: 'Overall Score',
      data: sortedData.map(d => d.overallScore),
      backgroundColor: sortedData.map(d => 
        getBarColor(d.overallScore, selectedAudit === d.auditType)
      ),
      borderColor: sortedData.map(d => 
        getBorderColor(d.overallScore, selectedAudit === d.auditType)
      ),
      borderWidth: sortedData.map(d => selectedAudit === d.auditType ? 3 : 1),
      borderRadius: 6,
      barThickness: 28,
    }],
  };

  const options: any = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        const item = sortedData[index];
        onBarClick(item.auditType, item.sessionId);
      }
    },
    plugins: {
      legend: { 
        display: false,
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 16,
          weight: 'bold' as const,
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        anchor: 'end',
        align: 'start',
        offset: 8,
        formatter: (value: any) => `${value.toFixed(1)}%`,
        color: '#fff',
        font: {
          weight: 'bold',
          size: 13,
        },
      },
    },
    scales: {
      x: { 
        beginAtZero: true, 
        max: 100,
        grid: { 
          color: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
        },
        ticks: {
          callback: (value: any) => `${value}%`,
          font: { size: 11 },
          color: isDarkMode ? '#FFFFFF' : '#374151',
        },
        title: {
          display: true,
          text: 'Score (%)',
          font: { weight: 'bold' as const, size: 12 },
          color: isDarkMode ? '#FFFFFF' : '#374151',
        },
      },
      y: { 
        grid: { 
          display: false,
        },
        ticks: { 
          font: (context: any) => {
            const index = context.index;
            if (index >= 0 && index < sortedData.length && selectedAudit === sortedData[index].auditType) {
              return { weight: 'bold' as const, size: 11 };
            }
            return { size: 11 };
          },
          color: (context: any) => {
            const index = context.index;
            if (index >= 0 && index < sortedData.length) {
              // Selected audit: blue highlight in both modes
              if (selectedAudit === sortedData[index].auditType) {
                return '#1e40af';
              }
              // Default text: white in dark mode, dark gray in light mode
              return isDarkMode ? '#FFFFFF' : '#374151';
            }
            return isDarkMode ? '#FFFFFF' : '#374151';
          },
        },
      },
    },
    onHover: (event: any, elements: any[]) => {
      const canvas = event.native?.target;
      if (canvas) {
        canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    },
  };

  // Calculate dynamic height based on number of items
  const chartHeight = Math.max(200, sortedData.length * 50 + 80);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Legend */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip 
          size="small" 
          label="High (80%+)" 
          sx={{ bgcolor: 'rgba(34, 197, 94, 0.8)', color: 'white', fontWeight: 500 }} 
        />
        <Chip 
          size="small" 
          label="Medium (50-79%)" 
          sx={{ bgcolor: 'rgba(245, 158, 11, 0.8)', color: 'white', fontWeight: 500 }} 
        />
        <Chip 
          size="small" 
          label="Low (<50%)" 
          sx={{ bgcolor: 'rgba(239, 68, 68, 0.8)', color: 'white', fontWeight: 500 }} 
        />
      </Box>

      {/* Instruction text */}
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
        Click on a bar to see detailed category breakdown below
      </Typography>

      {/* Chart */}
      <Box sx={{ width: '100%', height: chartHeight }}>
        <Bar data={chartData} options={options} />
      </Box>
    </Box>
  );
};

export default HorizontalBarChart;

